import {
  FormattedHTMLMessage,
  FormattedMessage,
  defineMessages,
  injectIntl,
} from 'react-intl';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';

import { accountLogout } from '../../../../ducks/user/auth';
import { claimOrder, fetchOrder } from '../../../../ducks/seatmap';
import { isMobile } from '../../../../mobile/helpers';
import { navigate } from '../../../../helpers';
import Loader from '../../components/Loader';
import Option from '../components/Option';
import styles from '../seatmap.scss';
import withUser from '../../../../components/withUser';

const propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      eventId: PropTypes.string,
      ticketId: PropTypes.string,
      orderId: PropTypes.string,
    }),
    url: PropTypes.string.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  order: PropTypes.shape({
    claimed_by: PropTypes.string,
    status: PropTypes.string,
    tickets: PropTypes.array,
    event: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
  event: PropTypes.shape({
    shortcode: PropTypes.string,
  }),
  loading: PropTypes.bool,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
};

const messages = defineMessages({
  manageTickets: {
    id: 'seatmap.connect.manageTickets',
    defaultMessage: 'Manage your tickets',
  },
  seePlaceGiveTickets: {
    id: 'seatmap.connect.seePlaceGiveTickets',
    defaultMessage: 'See, place, or give away your tickets.',
  },
  connectAccount: {
    id: 'seatmap.connect.connectAccount',
    defaultMessage: 'Connect your account',
  },
  loggedInAs: {
    id: 'seatmap.connect.loggedInAs',
    defaultMessage: 'You are logged in as {userName}.',
  },
  signOut: {
    id: 'seatmap.connect.signOut',
    defaultMessage: 'Sign out',
  },
  notYourAccount: {
    id: 'seatmap.connect.notYourAccount',
    defaultMessage: 'Not your account? Click here to sign out.',
  },
  signInOrReg: {
    id: 'seatmap.connect.signInOrReg',
    defaultMessage: 'Sign in or register',
  },
  redirectedSignInOrReg: {
    id: 'seatmap.connect.redirectedSignInOrReg',
    defaultMessage: 'You will be redirected to sign in to or register a DreamHack account.',
  },
});

class Connect extends React.Component {
  componentDidMount() {
    const { dispatch, match: { params } } = this.props;
    const { eventId, orderId } = params;
    dispatch(fetchOrder(eventId, orderId));
  }

  onClaimOrder() {
    const { dispatch, match: { params } } = this.props;
    const { eventId, orderId } = params;
    dispatch(claimOrder(eventId, orderId))
      .then((data) => {
        if (data) {
          this.gotoManage();
        }
      });
  }

  gotoManage() {
    const { event, history } = this.props;
    navigate(history, `/tickets/${event.shortcode}/manage`);
  }

  render() {
    const {
      user,
      history,
      match,
      error,
      order,
      event,
      loading,
      dispatch,
      intl,
    } = this.props;
    const tickets = order && order.tickets && Object.values(order.tickets);
    const { formatMessage } = intl;

    if (loading) {
      return <Loader loading={loading} />;
    }

    if (error) {
      return (
        <>
          <div className={styles.title}>
            {error}
          </div>
          { order && user && order.claimed_by === user.id
            ? (
              <>
                <div className={styles.description}>
                  <FormattedHTMLMessage
                    id="seatmap.connect.claimed.greeting"
                    defaultMessage="Hey <strong>{userName}</strong>! You seem to have already connected this order to your account."
                    values={{
                      userName: user && user.name,
                    }}
                  />
                </div>
                <Option
                  icon="ticket-alt"
                  title={formatMessage(messages.manageTickets)}
                  description={formatMessage(messages.seePlaceGiveTickets)}
                  onClick={() => this.gotoManage()}
                />
              </>
            )
            : (
              <div className={styles.description}>
                <FormattedMessage
                  id="seatmap.connect.claimed.mistake"
                  defaultMessage="Do you think this is a mistake? Please contact us at support@dreamhack.com with your order ID so we can help you out."
                />
              </div>
            )}
        </>
      );
    }

    if (event && order && (order.status === 'valid' || order.status === 'pending')) {
      return (
        <>
          <div className={styles.title}>
            <FormattedMessage
              id="seatmap.connect.order.title"
              defaultMessage="Connect your account"
            />
          </div>
          <div className={styles.description}>
            <FormattedHTMLMessage
              id="seatmap.connect.order.description"
              defaultMessage="This order contains <strong>{ticketCount, number}</strong> {ticketCount, plural, one {ticket} other {tickets}} for <strong>{eventName}</strong>. Connect the tickets to your DreamHack account to choose BYOC seats, give your tickets away, and use the DreamHack app."
              values={{
              // Tickets are categorized by arrays of ticket type, so we need
              // a little magic to count all of the tickets.
                ticketCount: (tickets && tickets?.length > 0 && tickets.reduce(
                  (accumulator, current) => (accumulator + current?.length),
                  0,
                )) || 0,
                eventName: order.event && order.event.name,
              }}
            />
          </div>
          { user
            ? (
              <>
                <Option
                  icon="link"
                  title={formatMessage(messages.connectAccount)}
                  description={formatMessage(messages.loggedInAs, { userName: user.name })}
                  onClick={() => this.onClaimOrder()}
                />
                <Option
                  icon="sign-out"
                  title={formatMessage(messages.signOut)}
                  description={formatMessage(messages.notYourAccount)}
                  onClick={isMobile() && window
                    ? window.logout : () => dispatch(accountLogout())}
                />
              </>
            )
            : (
              <Option
                icon="sign-in"
                title={formatMessage(messages.signInOrReg)}
                description={formatMessage(messages.redirectedSignInOrReg)}
                onClick={() => navigate(history, `/login/${btoa(match.url)}`)}
              />
            )}
        </>
      );
    }

    return (
      <>
        { (event && order && !(order.status === 'valid' || order.status === 'pending')) && (
          <>
            <div className={styles.title}>
              <FormattedMessage
                id="seatmap.connect.state.title"
                defaultMessage="Something is wrong"
              />
            </div>
            <div className={styles.description}>
              <FormattedHTMLMessage
                id="seatmap.connect.state.description"
                defaultMessage="This order is not currently valid. It's flagged as <strong>{status}</strong>. Please contact us at support@dreamhack.com with your order ID so we can help you out"
                values={{
                  status: order.status,
                }}
              />
            </div>
          </>
        )}
      </>
    );
  }
}

Connect.propTypes = propTypes;

function mapStateToProps(state) {
  return {
    event: state.getIn(['event', 'data']),
    order: state.getIn(['seatmap', 'order']),
    error: state.getIn(['seatmap', 'error']),
    loading: state.getIn(['seatmap', 'loading']),
  };
}

export default injectIntl(withUser(connect(mapStateToProps)(Connect)));
